import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { cn } from "@/lib/utils";
import { useParams } from "@tanstack/react-router";
import { ChevronRight, KeyRound } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Which company skills an agent's runs load: Metoděj's tab (with env vars) and lair's agents. */

type Skill = {
  name: string;
  description: string | null;
  enabled: boolean;
  envKeys?: string[];
  updatedAt: string | null;
  updatedBy: string | null;
};

type Patch = { enabled?: boolean; env?: Record<string, string | null> };

const ENV_KEY = /^[A-Z_][A-Z0-9_]{0,63}$/;

export function AgentSkillsPage() {
  const { t } = useTranslation();
  const { slug } = useParams({ strict: false }) as { slug: string };
  return (
    <SkillsList
      base={`/api/agents/${encodeURIComponent(slug)}/skills`}
      intro={t("agentTabs.skills.intro")}
      withEnv
    />
  );
}

/** `base` serves GET (the list) and PUT `<base>/<name>`. */
export function SkillsList({
  base,
  intro,
  withEnv = false,
}: {
  base: string;
  intro: string;
  withEnv?: boolean;
}) {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api
        .get<{ skills: Skill[] }>(base)
        .then((r) => setSkills(r.skills))
        .catch(() => setFailed(true)),
    [base],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const save = useApiMutation(
    (name: string, patch: Patch) =>
      api.put<{ skill: Skill }>(`${base}/${encodeURIComponent(name)}`, patch),
    {
      onSuccess: ({ skill }) => {
        setSkills((prev) => prev?.map((s) => (s.name === skill.name ? skill : s)) ?? prev);
        toast({ title: t("agentTabs.skills.saved") });
      },
      onError: (message) => toast({ title: message, variant: "destructive" }),
      fallbackMessage: t("agentTabs.skills.saveFailed"),
    },
  );

  if (failed) return <p className="text-destructive text-sm">{t("agentTabs.skills.loadFailed")}</p>;
  if (!skills)
    return <p className="text-muted-foreground text-sm">{t("agentTabs.skills.loading")}</p>;

  const q = query.trim().toLowerCase();
  const shown = q
    ? skills.filter((s) => `${s.name} ${s.description ?? ""}`.toLowerCase().includes(q))
    : skills;
  const on = skills.filter((s) => s.enabled).length;

  return (
    <div className="space-y-3" data-testid="agent-skills">
      <p className="text-muted-foreground text-sm">{intro}</p>
      {skills.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("agentTabs.skills.empty")}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm" data-testid="agent-skills-count">
              {t("agentTabs.skills.count", { on, total: skills.length })}
            </span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("agentTabs.skills.search")}
              aria-label={t("agentTabs.skills.search")}
              className="ml-auto h-8 w-64 rounded-md border border-input bg-background px-2 text-sm"
            />
          </div>
          <Card className="divide-y divide-border overflow-hidden p-0">
            {shown.map((s) => (
              <SkillRow
                key={s.name}
                skill={s}
                withEnv={withEnv}
                open={open === s.name}
                onToggleOpen={() => setOpen(open === s.name ? null : s.name)}
                busy={save.pending}
                onSave={(patch) => void save.mutate(s.name, patch)}
              />
            ))}
            {shown.length === 0 && (
              <p className="p-4 text-muted-foreground text-sm">{t("agentTabs.skills.noMatch")}</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Switch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-60",
        checked ? "bg-primary" : "bg-muted-foreground/30",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 rounded-full bg-background shadow transition-transform",
          checked ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

function SkillRow({
  skill,
  withEnv,
  open,
  onToggleOpen,
  busy,
  onSave,
}: {
  skill: Skill;
  withEnv: boolean;
  open: boolean;
  onToggleOpen: () => void;
  busy: boolean;
  onSave: (patch: Patch) => void;
}) {
  const { t } = useTranslation();
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const keyValid = ENV_KEY.test(key);

  return (
    <div data-testid={`agent-skill-${skill.name}`} className={cn(!skill.enabled && "bg-muted/30")}>
      <div className="flex items-center gap-3 px-4 py-2.5">
        <Switch
          checked={skill.enabled}
          disabled={busy}
          label={t("agentTabs.skills.toggle", { name: skill.name })}
          onChange={(enabled) => onSave({ enabled })}
        />
        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={open}
          aria-label={t("agentTabs.skills.details", { name: skill.name })}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span
            className={cn(
              "w-44 shrink-0 truncate font-medium font-mono text-sm",
              !skill.enabled && "text-muted-foreground",
            )}
          >
            {skill.name}
          </span>
          <span className="min-w-0 flex-1 truncate text-muted-foreground text-xs">
            {skill.description}
          </span>
          {!!skill.envKeys?.length && (
            <span
              className="flex shrink-0 items-center gap-1 text-muted-foreground text-xs"
              title={skill.envKeys.join(", ")}
            >
              <KeyRound className="h-3.5 w-3.5" />
              {skill.envKeys.length}
            </span>
          )}
          <ChevronRight
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-90",
            )}
          />
        </button>
      </div>

      {open && (
        <div className="space-y-3 border-border border-t bg-muted/20 px-4 py-3 pl-16">
          {skill.description && (
            <p className="whitespace-pre-line text-muted-foreground text-xs">{skill.description}</p>
          )}
          {withEnv && (
            <div className="space-y-2">
              <p className="font-medium text-xs">{t("agentTabs.skills.env")}</p>
              {!skill.envKeys?.length ? (
                <p className="text-muted-foreground text-xs">{t("agentTabs.skills.envNone")}</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {skill.envKeys.map((k) => (
                    <li key={k} className="flex items-center gap-1 rounded border px-2 py-0.5">
                      <span className="font-mono text-xs">{k}</span>
                      <span className="text-muted-foreground text-xs">••••</span>
                      <button
                        type="button"
                        className="text-muted-foreground text-xs hover:text-destructive"
                        disabled={busy}
                        aria-label={t("agentTabs.skills.remove", { key: k })}
                        onClick={() => onSave({ env: { [k]: null } })}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <form
                className="flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!keyValid || !value) return;
                  onSave({ env: { [key]: value } });
                  setKey("");
                  setValue("");
                }}
              >
                <input
                  value={key}
                  onChange={(e) => setKey(e.target.value.toUpperCase())}
                  placeholder={t("agentTabs.skills.keyPlaceholder")}
                  aria-label={t("agentTabs.skills.keyPlaceholder")}
                  className="h-8 w-48 rounded-md border border-input bg-background px-2 font-mono text-xs"
                />
                <input
                  type="password"
                  autoComplete="off"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t("agentTabs.skills.valuePlaceholder")}
                  aria-label={t("agentTabs.skills.valuePlaceholder")}
                  className="h-8 w-64 rounded-md border border-input bg-background px-2 text-xs"
                />
                <Button type="submit" size="sm" disabled={busy || !keyValid || !value}>
                  {t("agentTabs.skills.add")}
                </Button>
              </form>
              <p className="text-muted-foreground text-xs">{t("agentTabs.skills.envHelp")}</p>
            </div>
          )}
          {skill.updatedAt && (
            <p className="text-muted-foreground text-xs">
              {skill.updatedBy
                ? t("agentTabs.skills.changedBy", {
                    at: formatDateTime(skill.updatedAt),
                    who: skill.updatedBy,
                  })
                : t("agentTabs.skills.changedAt", { at: formatDateTime(skill.updatedAt) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
